import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ArrowLeft, Trash2, Clock, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { useFavorites } from '../contexts/FavoritesContext';
import { toast } from 'sonner';
import { looksLikeGroupedMealId, withGroupedParam } from './mealDetailView';
import { buildUrl, startFlowState } from '../utils/planLocation';

const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&h=600&fit=crop';

function Favorites() {
  const navigate = useNavigate();
  // P8: account-backed favorites — summaries come batched from the context
  // (no per-favorite meal-detail API fan-out).
  const { favorites: meals, loaded, removeFavorite: ctxRemoveFavorite } = useFavorites();
  const loading = !loaded;

  async function removeFavorite(mealId) {
    const ok = await ctxRemoveFavorite(mealId);
    if (ok) toast.success('Removed from favorites');
  }

  function handleImageError(e) {
    e.target.src = PLACEHOLDER_IMAGE;
  }

  // Grouped/rebuild-backed meals (AI photo logs, multi-food basket logs)
  // need the grouped marker so they open the same canonical grouped detail
  // experience they do everywhere else in the app — without it they'd
  // dispatch to the standard MealView, which assumes a plain macro/ingredient
  // shape (see AGENTS.md pre-launch audit, finding MEALS-3).
  function viewMeal(meal) {
    var path = '/meal-view/' + meal.meal_id;
    if (looksLikeGroupedMealId(meal.meal_id)) {
      navigate(path + '?' + withGroupedParam(''), { state: { grouped: true } });
    } else {
      navigate(path);
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen pb-bottom-nav">
        <div className="header-safe-top px-6 pb-6">
          <div className="flex items-center gap-4 mb-8">
            <Button
              onClick={function() { navigate(-1); }}
              variant="ghost"
              size="icon"
              className="rounded-full"
            >
              <ArrowLeft size={20} />
            </Button>
            <h1 className="font-heading font-bold text-2xl">Favorites</h1>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-bottom-nav" data-testid="favorites-page">
      <div className="header-safe-top px-6 pb-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            onClick={function() { navigate(-1); }}
            variant="ghost"
            size="icon"
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-2xl">Favorites</h1>
            <p className="text-sm text-muted-foreground">
              {meals.length} {meals.length === 1 ? 'meal' : 'meals'} saved
            </p>
          </div>
        </div>

        {/* Empty state */}
        {meals.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
            data-testid="empty-favorites"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
              <Heart size={32} className="text-muted-foreground" />
            </div>
            <h2 className="font-heading font-semibold text-lg mb-2">No favorites yet</h2>
            <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
              Tap the heart icon on any meal to save it here for quick access
            </p>
            <Button
              onClick={function() { navigate(buildUrl('/meals', { return: '/favorites' }), { state: startFlowState() }); }}
              className="rounded-full bg-accent text-white hover:bg-accent/90"
              data-testid="browse-meals-btn"
            >
              Browse Meals
            </Button>
          </motion.div>
        )}

        {/* Favorites grid */}
        {meals.length > 0 && (
          <div className="space-y-4">
            <AnimatePresence>
              {meals.map(function(meal, index) {
                return (
                  <motion.div
                    key={meal.meal_id}
                    initial={{ opacity: 0, y: 0 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-card border border-border/40 rounded-xl overflow-hidden"
                    data-testid={'favorite-meal-' + meal.meal_id}
                  >
                    <div className="flex">
                      {/* Image */}
                      <div 
                        className="w-28 h-28 flex-shrink-0 cursor-pointer"
                        onClick={function() { viewMeal(meal); }}
                      >
                        <img
                          src={meal.image_url || PLACEHOLDER_IMAGE}
                          alt={meal.name}
                          onError={handleImageError}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 p-4 flex flex-col justify-between">
                        <div>
                          <span className="text-xs text-accent font-medium uppercase tracking-wide">
                            {meal.category}
                          </span>
                          <h3 
                            className="font-heading font-semibold text-base mt-1 cursor-pointer hover:text-accent transition-colors"
                            onClick={function() { viewMeal(meal); }}
                          >
                            {meal.name}
                          </h3>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>{meal.calories} cal</span>
                            <span>•</span>
                            <span>{meal.protein}g protein</span>
                            {meal.cook_time_minutes > 0 && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Clock size={12} />
                                  {meal.cook_time_minutes}m
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col justify-between p-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={function() { removeFavorite(meal.meal_id); }}
                          className="rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          data-testid={'remove-favorite-' + meal.meal_id}
                        >
                          <Trash2 size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={function() { viewMeal(meal); }}
                          className="rounded-full text-muted-foreground hover:text-accent"
                          data-testid={'view-meal-' + meal.meal_id}
                        >
                          <ChevronRight size={18} />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Quick tip */}
        {meals.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 p-4 bg-accent/5 border border-accent/20 rounded-xl"
          >
            <p className="text-sm text-muted-foreground">
              <span className="text-accent font-medium">Tip:</span> Tap any meal to view the full recipe, or swipe left to remove from favorites.
            </p>
          </motion.div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

export default Favorites;
